import React, {useMemo} from 'react';

import ColorPicker from '../../../../components/ColorPicker';
import {Analytics} from '../../../../services/analytics';
import {toSorted} from '../../../../util/toSorted';
import {SettingSelect} from '../components/SettingSelect';
import {BucketCustomization} from '../types';
import {SemanticLegendPalette} from './utils/colorPalettes';
import {getColorFromValue} from './utils/getDefaultBucketColors';

const colorPickerStyle = {marginRight: '6px', marginBottom: '8px'};

const getCategoryColor = (
  categoryCustomization: BucketCustomization,
  index: number,
  totalCategories: number,
  semanticLegendPalette: SemanticLegendPalette
) => {
  return (
    categoryCustomization?.color ??
    getColorFromValue(index, totalCategories, semanticLegendPalette)
  );
};

export const CategoryCustomizationInput = ({
  category,
  categoryCustomization,
  categoriesList,
  numberOfCurrentCategories,
  index,
  semanticLegendPalette,
  updateCategorySetting,
  updateCategoryValue,
}: {
  updateCategorySetting: (
    settings: Partial<BucketCustomization>,
    index: number
  ) => void;
  updateCategoryValue: (
    selected: {value: string; label: string} | null,
    index: number
  ) => void;
  category: string;
  categoryCustomization: BucketCustomization;
  numberOfCurrentCategories: number;
  categoriesList: string[];
  index: number;
  semanticLegendPalette: SemanticLegendPalette;
}) => {
  const categoryColor = useMemo(() => {
    return getCategoryColor(
      categoryCustomization,
      index,
      numberOfCurrentCategories,
      semanticLegendPalette
    );
  }, [
    categoryCustomization,
    index,
    numberOfCurrentCategories,
    semanticLegendPalette,
  ]);

  const categoryOptions = useMemo(() => {
    return toSorted(categoriesList).map(category => ({
      value: category,
      label: category,
    }));
  }, [categoriesList]);

  const categoryValue = useMemo(() => {
    return {value: category, label: category};
  }, [category]);

  return (
    <div className="flex w-full gap-8" data-test="bucket-customization">
      <SettingSelect
        cacheOptions={false} // the options can be affected by status of the categories query, and the current key. We shouldn't cache the search results here.
        className="grow"
        options={categoryOptions}
        value={categoryValue}
        onChange={selected => {
          Analytics.track('Semantic legend category value changed', {
            newValue: selected?.value,
            index,
          });
          updateCategoryValue(selected, index);
        }}
      />
      <ColorPicker
        activeColor={categoryColor ?? ''}
        style={colorPickerStyle}
        setColor={color => {
          Analytics.track('Semantic legend category color changed', {
            newValue: color,
            index,
          });
          updateCategorySetting({color}, index);
        }}
        trigger="button"
      />
    </div>
  );
};
